import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { FiSave, FiEye, FiEyeOff, FiChevronDown, FiChevronUp, FiPlus, FiTrash2, FiArrowUp, FiArrowDown } from 'react-icons/fi';

const SECTION_LABELS = {
  hero: 'Hero Section',
  about: 'About Me',
  ventures: 'Ventures & Leadership',
  experience: 'Federal Experience',
  publications: 'Publications & Research',
  books: 'Published Books',
  education: 'Education',
  recognition: 'Awards & Recognition',
  contact: 'Contact Information'
};

// Reusable item card style
const itemCardStyle = {
  background: 'var(--bg-tertiary)',
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '14px',
  position: 'relative',
};

const itemHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '14px',
};

const itemActionsStyle = {
  display: 'flex',
  gap: '6px',
};

const smallBtnStyle = {
  background: 'none',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  padding: '4px 8px',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  fontSize: '0.82rem',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const deleteBtnStyle = {
  ...smallBtnStyle,
  color: 'var(--error)',
  borderColor: 'rgba(197, 48, 48, 0.3)',
};

const addBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 18px',
  background: 'var(--accent-glow)',
  border: '1px dashed var(--accent-primary)',
  borderRadius: '10px',
  color: 'var(--accent-primary)',
  fontWeight: 600,
  fontSize: '0.88rem',
  cursor: 'pointer',
  width: '100%',
  justifyContent: 'center',
  marginTop: '8px',
};

const fieldRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
};

// ===== VENTURES EDITOR =====
const VenturesEditor = ({ content, onChange }) => {
  const ventures = content?.ventures || [];

  const updateVenture = (idx, field, value) => {
    const updated = [...ventures];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...content, ventures: updated });
  };

  const addVenture = () => {
    onChange({ ...content, ventures: [...ventures, { name: '', role: '', description: '', url: '' }] });
  };

  const removeVenture = (idx) => {
    if (window.confirm(`Delete "${ventures[idx].name || 'this venture'}"?`)) {
      onChange({ ...content, ventures: ventures.filter((_, i) => i !== idx) });
    }
  };

  const moveVenture = (idx, dir) => {
    const updated = [...ventures];
    const target = idx + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    onChange({ ...content, ventures: updated });
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Ventures ({ventures.length})
      </label>
      {ventures.map((v, idx) => (
        <div key={idx} style={itemCardStyle}>
          <div style={itemHeaderStyle}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
              {v.name || `Venture #${idx + 1}`}
            </span>
            <div style={itemActionsStyle}>
              <button style={smallBtnStyle} onClick={() => moveVenture(idx, -1)} title="Move up"><FiArrowUp size={14} /></button>
              <button style={smallBtnStyle} onClick={() => moveVenture(idx, 1)} title="Move down"><FiArrowDown size={14} /></button>
              <button style={deleteBtnStyle} onClick={() => removeVenture(idx)} title="Delete"><FiTrash2 size={14} /></button>
            </div>
          </div>
          <div style={fieldRowStyle}>
            <div className="editor-field">
              <label>Name</label>
              <input value={v.name || ''} onChange={(e) => updateVenture(idx, 'name', e.target.value)} placeholder="e.g. ChatWithDr.com" />
            </div>
            <div className="editor-field">
              <label>Role</label>
              <input value={v.role || ''} onChange={(e) => updateVenture(idx, 'role', e.target.value)} placeholder="e.g. Founder & CEO" />
            </div>
          </div>
          <div className="editor-field">
            <label>Description</label>
            <textarea value={v.description || ''} onChange={(e) => updateVenture(idx, 'description', e.target.value)} rows={2} placeholder="Brief description of the venture" />
          </div>
          <div className="editor-field">
            <label>Website URL</label>
            <input value={v.url || ''} onChange={(e) => updateVenture(idx, 'url', e.target.value)} placeholder="https://example.com" />
          </div>
        </div>
      ))}
      <button style={addBtnStyle} onClick={addVenture}><FiPlus size={16} /> Add Venture</button>
    </div>
  );
};

// ===== AGENCIES EDITOR =====
const AgenciesEditor = ({ content, onChange }) => {
  const agencies = content?.agencies || [];

  const updateAgency = (idx, field, value) => {
    const updated = [...agencies];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...content, agencies: updated });
  };

  const addAgency = () => {
    onChange({ ...content, agencies: [...agencies, { name: '', fullName: '', icon: '🏛️' }] });
  };

  const removeAgency = (idx) => {
    if (window.confirm(`Delete "${agencies[idx].name || 'this agency'}"?`)) {
      onChange({ ...content, agencies: agencies.filter((_, i) => i !== idx) });
    }
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Federal Agencies ({agencies.length})
      </label>
      {agencies.map((a, idx) => (
        <div key={idx} style={{ ...itemCardStyle, padding: '14px 20px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div className="editor-field" style={{ width: '60px', marginBottom: 0 }}>
              <input value={a.icon || ''} onChange={(e) => updateAgency(idx, 'icon', e.target.value)} style={{ textAlign: 'center', fontSize: '1.3rem', padding: '8px' }} />
            </div>
            <div className="editor-field" style={{ width: '100px', marginBottom: 0 }}>
              <input value={a.name || ''} onChange={(e) => updateAgency(idx, 'name', e.target.value)} placeholder="Abbr" />
            </div>
            <div className="editor-field" style={{ flex: 1, marginBottom: 0 }}>
              <input value={a.fullName || ''} onChange={(e) => updateAgency(idx, 'fullName', e.target.value)} placeholder="Full name" />
            </div>
            <button style={deleteBtnStyle} onClick={() => removeAgency(idx)}><FiTrash2 size={14} /></button>
          </div>
        </div>
      ))}
      <button style={addBtnStyle} onClick={addAgency}><FiPlus size={16} /> Add Agency</button>
    </div>
  );
};

// ===== EDUCATION EDITOR =====
const EducationEditor = ({ content, onChange }) => {
  const degrees = content?.degrees || [];

  const updateDegree = (idx, field, value) => {
    const updated = [...degrees];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...content, degrees: updated });
  };

  const addDegree = () => {
    onChange({ ...content, degrees: [...degrees, { degree: '', field: '', university: '', icon: '🎓' }] });
  };

  const removeDegree = (idx) => {
    if (window.confirm(`Delete "${degrees[idx].degree || 'this degree'}"?`)) {
      onChange({ ...content, degrees: degrees.filter((_, i) => i !== idx) });
    }
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Degrees ({degrees.length})
      </label>
      {degrees.map((d, idx) => (
        <div key={idx} style={itemCardStyle}>
          <div style={itemHeaderStyle}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{d.degree || `Degree #${idx + 1}`}</span>
            <button style={deleteBtnStyle} onClick={() => removeDegree(idx)}><FiTrash2 size={14} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr', gap: '12px' }}>
            <div className="editor-field">
              <label>Icon</label>
              <input value={d.icon || ''} onChange={(e) => updateDegree(idx, 'icon', e.target.value)} style={{ textAlign: 'center', fontSize: '1.3rem' }} />
            </div>
            <div className="editor-field">
              <label>Degree</label>
              <input value={d.degree || ''} onChange={(e) => updateDegree(idx, 'degree', e.target.value)} placeholder="e.g. DrPH" />
            </div>
            <div className="editor-field">
              <label>Field</label>
              <input value={d.field || ''} onChange={(e) => updateDegree(idx, 'field', e.target.value)} placeholder="e.g. Public Health" />
            </div>
          </div>
          <div className="editor-field">
            <label>University</label>
            <input value={d.university || ''} onChange={(e) => updateDegree(idx, 'university', e.target.value)} placeholder="e.g. Walden University" />
          </div>
        </div>
      ))}
      <button style={addBtnStyle} onClick={addDegree}><FiPlus size={16} /> Add Degree</button>
    </div>
  );
};

// ===== AWARDS EDITOR =====
const AwardsEditor = ({ content, onChange }) => {
  const awards = content?.awards || [];

  const updateAward = (idx, field, value) => {
    const updated = [...awards];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...content, awards: updated });
  };

  const addAward = () => {
    onChange({ ...content, awards: [...awards, { title: '', org: '', year: '', description: '' }] });
  };

  const removeAward = (idx) => {
    if (window.confirm(`Delete "${awards[idx].title || 'this award'}"?`)) {
      onChange({ ...content, awards: awards.filter((_, i) => i !== idx) });
    }
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Awards ({awards.length})
      </label>
      {awards.map((a, idx) => (
        <div key={idx} style={itemCardStyle}>
          <div style={itemHeaderStyle}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{a.title || `Award #${idx + 1}`}</span>
            <button style={deleteBtnStyle} onClick={() => removeAward(idx)}><FiTrash2 size={14} /></button>
          </div>
          <div style={fieldRowStyle}>
            <div className="editor-field">
              <label>Title</label>
              <input value={a.title || ''} onChange={(e) => updateAward(idx, 'title', e.target.value)} placeholder="Award title" />
            </div>
            <div className="editor-field">
              <label>Year</label>
              <input value={a.year || ''} onChange={(e) => updateAward(idx, 'year', e.target.value)} placeholder="e.g. 2024" />
            </div>
          </div>
          <div className="editor-field">
            <label>Organization</label>
            <input value={a.org || ''} onChange={(e) => updateAward(idx, 'org', e.target.value)} placeholder="Awarding organization" />
          </div>
          <div className="editor-field">
            <label>Description</label>
            <textarea value={a.description || ''} onChange={(e) => updateAward(idx, 'description', e.target.value)} rows={2} placeholder="Brief description" />
          </div>
        </div>
      ))}
      <button style={addBtnStyle} onClick={addAward}><FiPlus size={16} /> Add Award</button>
    </div>
  );
};

// ===== PUBLICATIONS EDITOR =====
const PublicationsEditor = ({ content, onChange }) => {
  const featured = content?.featured || [];
  const other = content?.other || [];

  const updateFeatured = (idx, field, value) => {
    const updated = [...featured];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...content, featured: updated });
  };

  const addFeatured = () => {
    onChange({ ...content, featured: [...featured, { title: '', journal: '', date: '', url: '' }] });
  };

  const removeFeatured = (idx) => {
    if (window.confirm('Delete this featured publication?')) {
      onChange({ ...content, featured: featured.filter((_, i) => i !== idx) });
    }
  };

  const updateOther = (idx, field, value) => {
    const updated = [...other];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...content, other: updated });
  };

  const addOther = () => {
    onChange({ ...content, other: [...other, { title: '', date: '' }] });
  };

  const removeOther = (idx) => {
    if (window.confirm('Delete this publication?')) {
      onChange({ ...content, other: other.filter((_, i) => i !== idx) });
    }
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Featured Publications ({featured.length})
      </label>
      {featured.map((p, idx) => (
        <div key={idx} style={itemCardStyle}>
          <div style={itemHeaderStyle}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.88rem' }}>{p.title ? p.title.substring(0, 50) + '...' : `Publication #${idx + 1}`}</span>
            <button style={deleteBtnStyle} onClick={() => removeFeatured(idx)}><FiTrash2 size={14} /></button>
          </div>
          <div className="editor-field">
            <label>Title</label>
            <input value={p.title || ''} onChange={(e) => updateFeatured(idx, 'title', e.target.value)} placeholder="Publication title" />
          </div>
          <div style={fieldRowStyle}>
            <div className="editor-field">
              <label>Journal</label>
              <input value={p.journal || ''} onChange={(e) => updateFeatured(idx, 'journal', e.target.value)} placeholder="e.g. Military Medicine" />
            </div>
            <div className="editor-field">
              <label>Date</label>
              <input value={p.date || ''} onChange={(e) => updateFeatured(idx, 'date', e.target.value)} placeholder="e.g. August 2021" />
            </div>
          </div>
          <div className="editor-field">
            <label>URL</label>
            <input value={p.url || ''} onChange={(e) => updateFeatured(idx, 'url', e.target.value)} placeholder="https://..." />
          </div>
        </div>
      ))}
      <button style={addBtnStyle} onClick={addFeatured}><FiPlus size={16} /> Add Featured Publication</button>

      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', marginTop: '28px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Other Publications ({other.length})
      </label>
      {other.map((p, idx) => (
        <div key={idx} style={{ ...itemCardStyle, padding: '14px 20px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div className="editor-field" style={{ flex: 1, marginBottom: 0 }}>
              <input value={p.title || ''} onChange={(e) => updateOther(idx, 'title', e.target.value)} placeholder="Publication title" />
            </div>
            <div className="editor-field" style={{ width: '150px', marginBottom: 0 }}>
              <input value={p.date || ''} onChange={(e) => updateOther(idx, 'date', e.target.value)} placeholder="Date" />
            </div>
            <button style={deleteBtnStyle} onClick={() => removeOther(idx)}><FiTrash2 size={14} /></button>
          </div>
        </div>
      ))}
      <button style={addBtnStyle} onClick={addOther}><FiPlus size={16} /> Add Other Publication</button>

      <div className="editor-field" style={{ marginTop: '20px' }}>
        <label>Amazon Author Page URL</label>
        <input
          value={content?.amazonAuthorPage || ''}
          onChange={(e) => onChange({ ...content, amazonAuthorPage: e.target.value })}
          placeholder="https://www.amazon.com/stores/..."
        />
      </div>
    </div>
  );
};

// ===== ABOUT HIGHLIGHTS EDITOR =====
const HighlightsEditor = ({ highlights, onChange }) => {
  const items = highlights || [];

  const update = (idx, value) => {
    const updated = [...items];
    updated[idx] = value;
    onChange(updated);
  };

  const add = () => onChange([...items, '']);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Highlights ({items.length})
      </label>
      {items.map((h, idx) => (
        <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input
            value={h}
            onChange={(e) => update(idx, e.target.value)}
            placeholder="Highlight text"
            style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.92rem', outline: 'none' }}
          />
          <button style={deleteBtnStyle} onClick={() => remove(idx)}><FiTrash2 size={14} /></button>
        </div>
      ))}
      <button style={addBtnStyle} onClick={add}><FiPlus size={16} /> Add Highlight</button>
    </div>
  );
};

// ===== MAIN ADMIN CONTENT COMPONENT =====
const AdminContent = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await api.get('/content/all');
      setSections(res.data);
    } catch (err) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const updateSection = (index, field, value) => {
    const updated = [...sections];
    if (field.startsWith('content.')) {
      const contentField = field.replace('content.', '');
      updated[index] = {
        ...updated[index],
        content: { ...updated[index].content, [contentField]: value }
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setSections(updated);
  };

  const updateSectionContent = (index, newContent) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], content: newContent };
    setSections(updated);
  };

  const saveSection = async (section) => {
    setSaving({ ...saving, [section.section]: true });
    try {
      await api.put(`/content/${section.section}`, {
        title: section.title,
        subtitle: section.subtitle,
        content: section.content,
        isVisible: section.isVisible,
        order: section.order
      });
      toast.success(`${SECTION_LABELS[section.section]} saved!`);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving({ ...saving, [section.section]: false });
    }
  };

  const toggleVisibility = async (index) => {
    const updated = [...sections];
    updated[index].isVisible = !updated[index].isVisible;
    setSections(updated);
    await saveSection(updated[index]);
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="admin-page-header">
        <h1>Edit Content</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Click a section to expand and edit
        </p>
      </div>

      {sections.map((section, index) => (
        <div key={section.section} className="editor-section">
          <h3>
            <span
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}
              onClick={() => setExpandedSection(expandedSection === section.section ? null : section.section)}
            >
              {expandedSection === section.section ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              {SECTION_LABELS[section.section] || section.section}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className={`admin-btn ${section.isVisible ? 'admin-btn-secondary' : 'admin-btn-danger'} toggle-visibility`}
                onClick={() => toggleVisibility(index)}
              >
                {section.isVisible ? <><FiEye size={14} /> Visible</> : <><FiEyeOff size={14} /> Hidden</>}
              </button>
              <button
                className="admin-btn admin-btn-primary"
                style={{ fontSize: '0.82rem', padding: '6px 14px' }}
                onClick={() => saveSection(section)}
                disabled={saving[section.section]}
              >
                {saving[section.section] ? 'Saving...' : <><FiSave size={14} /> Save</>}
              </button>
            </div>
          </h3>

          {expandedSection === section.section && (
            <div style={{ marginTop: '18px' }}>
              {/* Title & Subtitle for all sections */}
              <div className="editor-field">
                <label>Section Title</label>
                <input
                  value={section.title || ''}
                  onChange={(e) => updateSection(index, 'title', e.target.value)}
                  placeholder="Section title"
                />
              </div>
              <div className="editor-field">
                <label>Subtitle</label>
                <input
                  value={section.subtitle || ''}
                  onChange={(e) => updateSection(index, 'subtitle', e.target.value)}
                  placeholder="Section subtitle"
                />
              </div>

              {/* ===== HERO ===== */}
              {section.section === 'hero' && (
                <>
                  <div className="editor-field">
                    <label>Tagline</label>
                    <input
                      value={section.content?.tagline || ''}
                      onChange={(e) => updateSection(index, 'content.tagline', e.target.value)}
                    />
                  </div>
                  <div className="editor-field">
                    <label>Description</label>
                    <textarea
                      value={section.content?.description || ''}
                      onChange={(e) => updateSection(index, 'content.description', e.target.value)}
                      rows={3}
                    />
                  </div>
                </>
              )}

              {/* ===== ABOUT ===== */}
              {section.section === 'about' && (
                <>
                  <div className="editor-field">
                    <label>Bio</label>
                    <textarea
                      value={section.content?.bio || ''}
                      onChange={(e) => updateSection(index, 'content.bio', e.target.value)}
                      rows={5}
                    />
                  </div>
                  <HighlightsEditor
                    highlights={section.content?.highlights}
                    onChange={(newHighlights) => updateSectionContent(index, { ...section.content, highlights: newHighlights })}
                  />
                </>
              )}

              {/* ===== VENTURES ===== */}
              {section.section === 'ventures' && (
                <VenturesEditor
                  content={section.content}
                  onChange={(newContent) => updateSectionContent(index, newContent)}
                />
              )}

              {/* ===== EXPERIENCE ===== */}
              {section.section === 'experience' && (
                <AgenciesEditor
                  content={section.content}
                  onChange={(newContent) => updateSectionContent(index, newContent)}
                />
              )}

              {/* ===== PUBLICATIONS ===== */}
              {section.section === 'publications' && (
                <PublicationsEditor
                  content={section.content}
                  onChange={(newContent) => updateSectionContent(index, newContent)}
                />
              )}

              {/* ===== BOOKS ===== */}
              {section.section === 'books' && (
                <>
                  <div className="editor-field">
                    <label>Book Count</label>
                    <input
                      value={section.content?.count || ''}
                      onChange={(e) => updateSection(index, 'content.count', e.target.value)}
                    />
                  </div>
                  <div className="editor-field">
                    <label>Description</label>
                    <textarea
                      value={section.content?.description || ''}
                      onChange={(e) => updateSection(index, 'content.description', e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="editor-field">
                    <label>Amazon Author Page URL</label>
                    <input
                      value={section.content?.amazonUrl || ''}
                      onChange={(e) => updateSection(index, 'content.amazonUrl', e.target.value)}
                    />
                  </div>
                </>
              )}

              {/* ===== EDUCATION ===== */}
              {section.section === 'education' && (
                <EducationEditor
                  content={section.content}
                  onChange={(newContent) => updateSectionContent(index, newContent)}
                />
              )}

              {/* ===== RECOGNITION ===== */}
              {section.section === 'recognition' && (
                <AwardsEditor
                  content={section.content}
                  onChange={(newContent) => updateSectionContent(index, newContent)}
                />
              )}

              {/* ===== CONTACT ===== */}
              {section.section === 'contact' && (
                <>
                  <div className="editor-field">
                    <label>Contact Email</label>
                    <input
                      value={section.content?.email || ''}
                      onChange={(e) => updateSection(index, 'content.email', e.target.value)}
                    />
                  </div>
                  <div className="editor-field">
                    <label>Contact Message</label>
                    <textarea
                      value={section.content?.message || ''}
                      onChange={(e) => updateSection(index, 'content.message', e.target.value)}
                      rows={3}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminContent;